import React from 'react';
import { Button, Grid } from '@mui/material';
import { Form } from 'formik';
import { LocationInAppLinksCardComponent } from 'components/location/cards/LocationInAppLinksCardComponent';
import { Location } from 'components/location/models/LocationModel';
import { MuiForm } from 'lib/Form';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { Row } from 'ui/Flex';
import { Panel } from 'ui/Panel';
import { locationApi } from '../LocationApi';

export interface LocationInAppLinksCardProps {
    currentLocation: Location;
    setCurrentLocation: (location: Location) => void;
}

interface LocationInAppLinksFormValues {
    facebook: string;
    twitter: string;
    instagram: string;
    citymapper: boolean;
    uber: boolean;
}

export const LocationInAppLinksCard: React.FC<LocationInAppLinksCardProps> = ({
    currentLocation,
    setCurrentLocation
}) => {
    const [isEditing, setIsEditing] = React.useState(false);

    const initialValues: LocationInAppLinksFormValues = React.useMemo(
        () => ({
            facebook: currentLocation.links?.facebook?.parameters ?? '',
            twitter: currentLocation.links?.twitter?.parameters ?? '',
            instagram: currentLocation.links?.instagram?.parameters ?? '',
            citymapper: currentLocation.links?.citymapper?.enabled ?? false,
            uber: currentLocation.links?.uber?.enabled ?? false
        }),
        [currentLocation.links]
    );

    const handleSubmit = React.useCallback(
        async (values: LocationInAppLinksFormValues) => {
            const locationId = currentLocation._id;
            const { facebook, twitter, instagram, citymapper, uber } = values;

            const response = await locationApi.update(locationId, {
                body: {
                    links: {
                        facebook: { enabled: !!facebook, parameters: facebook },
                        twitter: { enabled: !!twitter, parameters: twitter },
                        instagram: { enabled: !!instagram, parameters: instagram },
                        citymapper: { enabled: citymapper },
                        uber: { enabled: uber },
                        hailo: { enabled: false },
                        foursquare: { enabled: false }
                    }
                }
            });
            if (response.ok) {
                setCurrentLocation(response.body);
            }
            setIsEditing(false);
        },
        [currentLocation._id, setCurrentLocation]
    );

    const toggleState = React.useCallback(() => {
        setIsEditing(currentValue => !currentValue);
    }, []);

    return (
        <Panel title="Links" showAction={!isEditing} onClick={toggleState} divider>
            {isEditing ? (
                <MuiForm<LocationInAppLinksFormValues>
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ submitForm, isSubmitting }) => (
                        <Form>
                            <Grid container spacing={2}>
                                <Grid
                                    size={{
                                        md: 6,
                                        xs: 12
                                    }}
                                >
                                    <Grid container spacing={1}>
                                        <Grid size={12}>
                                            <TextFormField
                                                name="facebook"
                                                label="Facebook"
                                                description="This is the ID of the Facebook page you want to link
                                                        to the location."
                                            />
                                        </Grid>
                                        <Grid size={12}>
                                            <TextFormField
                                                name="twitter"
                                                label="Twitter"
                                                description="This is the username of the Twitter account you want to link to the location."
                                            />
                                        </Grid>
                                        <Grid size={12}>
                                            <TextFormField
                                                name="instagram"
                                                label="Instagram"
                                                description="This is the username of the Instagram page you want to link to the location."
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid
                                    size={{
                                        md: 6,
                                        xs: 12
                                    }}
                                >
                                    <Grid container spacing={1}>
                                        <Grid size={12}>
                                            <SwitchFormField name="uber" label="Uber" />
                                        </Grid>
                                        <Grid size={12}>
                                            <SwitchFormField name="citymapper" label="Citymapper" />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid size={12}>
                                    <Row gutter align="flex-end">
                                        <Button onClick={toggleState} variant="outlined">
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={submitForm}
                                            disabled={isSubmitting}
                                            variant="contained"
                                            color="primary"
                                        >
                                            Submit
                                        </Button>
                                    </Row>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </MuiForm>
            ) : (
                <LocationInAppLinksCardComponent location={currentLocation} />
            )}
        </Panel>
    );
};
